import React from 'react';
import { caseStudies } from '../data/mock';
import { MapPin, Building2, Store, Calendar } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Map study IDs to translation keys
const studyKeyMap = {
  1: 'ocmax',
  2: 'borymall',
  3: 'petrzalska',
  4: 'shoppingpalace',
  5: 'novum',
  6: 'schron'
};

const CaseStudyCard = ({ study, t }) => {
  const studyKey = studyKeyMap[study.id];
  const whatWeDid = t(`caseStudies.studies.${studyKey}.whatWeDid`) || study.whatWeDid;
  const outcome = t(`caseStudies.studies.${studyKey}.outcome`) || study.outcome;

  return (
    <div className="case-card card-hover bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm">
      {/* Image */}
      <div className="relative h-32 sm:h-48 overflow-hidden">
        <img 
          src={study.image}
          alt={study.name}
          className="case-card-image w-full h-full object-cover"
        />
        <div className="image-overlay absolute inset-0" />
      </div>

      {/* Content */}
      <div className="p-3 sm:p-6">
        {/* Tags */}
        <div className="flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-4">
          {study.tags.map((tag) => (
            <span 
              key={tag}
              className="tag-chip px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-medium text-[#1a237e]"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Name & Details */}
        <h3 className="text-sm sm:text-xl font-bold text-[#0d1442] mb-2 sm:mb-3">{study.name}</h3>
        
        <div className="space-y-1 sm:space-y-2 mb-2 sm:mb-4 hidden sm:block">
          {study.locations && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <MapPin className="w-4 h-4 text-[#5c6bc0]" />
              {study.locations}
            </div>
          )}
          {study.owner && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Building2 className="w-4 h-4 text-[#5c6bc0]" />
              {study.owner}
            </div>
          )}
          {study.gla && (
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Store className="w-4 h-4 text-[#5c6bc0]" />
              GLA {study.gla} • {study.shops} {t('caseStudies.shops') || 'shops'}
            </div>
          )}
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Calendar className="w-4 h-4 text-[#5c6bc0]" />
            {study.since || study.period}
          </div>
        </div>

        {/* What we did & Outcome */}
        <div className="space-y-2 sm:space-y-3 pt-2 sm:pt-4 border-t border-slate-100">
          <div>
            <span className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('caseStudies.whatWeDid')}</span>
            <p className="text-xs sm:text-sm text-slate-700 mt-0.5 sm:mt-1 line-clamp-2">{whatWeDid}</p>
          </div>
          <div>
            <span className="text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">{t('caseStudies.outcome')}</span>
            <p className="text-xs sm:text-sm text-[#1a237e] font-medium mt-0.5 sm:mt-1 line-clamp-2">{outcome}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const CaseStudiesSection = () => {
  const { t } = useLanguage();

  return (
    <section id="case-studies" className="py-16 bg-slate-50">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-10">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0d1442] mb-4">
            {t('caseStudies.title')} <span className="gradient-text">{t('caseStudies.titleHighlight')}</span>
          </h2>
        </div>

        {/* Grid - 6 case studies, always minimum 2 columns */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {caseStudies.map((study) => (
            <CaseStudyCard key={study.id} study={study} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudiesSection;
